import { useState, useMemo, useEffect } from "react";
import {
  useRouter,
  useNavigate,
  useParams,
  createFileRoute,
} from "@tanstack/react-router";
import { useGetProfile } from "@/lib/api/auth/hooks";

// FSD imports
import {
  useNoticeCategories,
  useNotice,
  useUpdateNotice,
} from "@/features/notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { DocEditor, isDocEmpty } from "@growing/editor";
import "@growing/editor/styles.css";
import { createDocAssetAdapter } from "@growing/api";
import { apiClient } from "@/lib/api/client";
import type { DocContent } from "@growing/types";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { toast } from "sonner";

function EditNoticePage() {
  const router = useRouter();
  const navigate = useNavigate();
  const { id } = useParams({ strict: false }) as { id: string };
  // 편집 중인 본문. null이면 아직 손대지 않은 상태라 원본을 그대로 저장한다.
  const [editedDoc, setEditedDoc] = useState<DocContent | null>(null);

  const { data: profile } = useGetProfile();
  const userId = profile?._id || "";

  const { data: notice, isLoading: noticeLoading } = useNotice(id);
  const { data: categories } = useNoticeCategories();
  const updateNotice = useUpdateNotice();

  const assetAdapter = useMemo(() => createDocAssetAdapter(apiClient), []);

  // notice 데이터가 로드된 후 초기값 설정
  const [title, setTitle] = useState(() => notice?.title || "");
  const [categoryId, setCategoryId] = useState(
    notice
      ? typeof notice.category === "object"
        ? notice.category._id
        : notice.category
      : ""
  );
  const [isPinned, setIsPinned] = useState(notice?.isPinned || false);

  // notice 데이터가 변경되면 상태 업데이트 (초기 로딩 시에만)
  useEffect(() => {
    if (notice) {
      setTitle(notice.title);
      setCategoryId(
        typeof notice.category === "object"
          ? notice.category._id
          : notice.category
      );
      setIsPinned(notice.isPinned);
    }
    // TODO: Next.js key 값 설정해서 의존성 배열 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notice?.title]); // notice가 처음 로드될 때만 실행

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("제목을 입력해주세요.");
      return;
    }
    if (!categoryId) {
      toast.error("카테고리를 선택해주세요.");
      return;
    }

    const doc = editedDoc ?? notice?.doc;
    if (!doc || isDocEmpty(doc)) {
      toast.error("내용을 입력해주세요.");
      return;
    }

    try {
      await updateNotice.mutateAsync({
        id,
        data: {
          title,
          category: categoryId,
          doc,
          isPinned,
          lastModifiedBy: userId,
        },
      });
      toast.success("공지사항이 수정되었습니다.");
      navigate({ to: `/notice/${id}` });
    } catch {
      toast.error("공지사항 수정에 실패했습니다.");
    }
  };

  if (noticeLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-32 mb-8" />
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-[600px] w-full rounded-lg" />
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!notice) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">공지사항을 찾을 수 없습니다.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header title="공지사항 수정" />

      <div className="flex flex-col gap-6">
        <div className="space-y-6">
          {/* 카테고리 선택 */}
          <div className="space-y-2">
            <Label htmlFor="category">카테고리</Label>
            <Combobox
              items={categories ?? []}
              value={categories?.find((c) => c._id === categoryId) ?? null}
              onValueChange={(category) => setCategoryId(category?._id ?? "")}
              itemToStringLabel={(c) => c.name}
              isItemEqualToValue={(a, b) => a._id === b._id}
            >
              <ComboboxInput placeholder="카테고리를 선택하세요" />
              <ComboboxContent>
                <ComboboxEmpty>결과 없음</ComboboxEmpty>
                <ComboboxList>
                  {(category) => (
                    <ComboboxItem key={category._id} value={category}>
                      {category.name}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>

          {/* 제목 */}
          <div className="space-y-2">
            <Label htmlFor="title">제목</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목을 입력하세요"
            />
          </div>

          {/* 상단 고정 */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPinned"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="size-4"
            />
            <Label htmlFor="isPinned" className="cursor-pointer">
              상단 고정
            </Label>
          </div>

          {/* 에디터 */}
          <div className="space-y-2">
            <Label>내용</Label>
            {/* DocEditor는 비제어라 value는 초기값이다. 다른 공지를 열면 key로 리마운트된다 */}
            <DocEditor
              key={notice._id}
              value={notice.doc}
              adapter={assetAdapter}
              placeholder="본문을 입력하세요. 이미지는 붙여넣거나 끌어다 놓을 수 있습니다."
              onChange={({ doc: next }) => setEditedDoc(next)}
              onError={(message) => toast.error(message)}
            />
          </div>

          {/* 버튼 */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => router.history.back()}>
              취소
            </Button>
            <Button onClick={handleSubmit} disabled={updateNotice.isPending}>
              {updateNotice.isPending ? "수정 중..." : "수정하기"}
            </Button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/notice/$id/edit")({
  component: EditNoticePage,
});
